/**
 * ColorPicker Component
 *
 * Modern color picker with a dropdown portal, predefined palette,
 * transparent option, and custom color picker.
 *
 * Features:
 * - 24 predefined colors in a 6x4 grid with large rounded buttons
 * - Dropdown portal for proper z-index handling
 * - Transparent option
 * - Custom color picker button
 * - Clean, modern design matching the options panel system
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Palette, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { COLOR_PALETTE, getColorName, isCustomColor } from '../../../constants/colorPalette';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  showTransparent?: boolean;
  showOpacity?: boolean;
  mode?: 'inline' | 'popover';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  className?: string;
  showThemeOption?: boolean;
  themeResolvedColor?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  showTransparent = true,
  showOpacity = false,
  mode = 'inline',
  size = 'md',
  label,
  className,
  showThemeOption = false,
  themeResolvedColor
}) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [hexDraft, setHexDraft] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse color value to extract RGB hex and opacity
  const parseColor = (colorValue: string): { hex: string; opacity: number } => {
    if (colorValue === 'transparent') {
      return { hex: '#FFFFFF', opacity: 0 };
    }

    if (colorValue === 'theme') {
      return { hex: themeResolvedColor || '#FFFFFF', opacity: 100 };
    }

    // 8-digit hex with alpha: #RRGGBBAA
    if (colorValue.length === 9 && colorValue.startsWith('#')) {
      const hex = colorValue.slice(0, 7);
      const alpha = parseInt(colorValue.slice(7, 9), 16);
      const opacity = Math.round((alpha / 255) * 100);
      return { hex, opacity };
    }

    return { hex: colorValue, opacity: 100 };
  };

  // Combine RGB hex + opacity to 8-digit hex
  const combineColorOpacity = (hex: string, opacity: number): string => {
    if (opacity === 0) return 'transparent';

    const cleanHex = hex.startsWith('#') ? hex : `#${hex}`;
    const hexOnly = cleanHex.slice(0, 7);

    const alpha = Math.round((opacity / 100) * 255);
    const alphaHex = alpha.toString(16).padStart(2, '0').toUpperCase();

    if (opacity === 100) return hexOnly;

    return `${hexOnly}${alphaHex}`;
  };

  const { hex: currentHex, opacity: currentOpacity } = parseColor(value);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 320;
    const dropdownWidth = 280;
    const padding = 8;

    let top = rect.bottom + padding;
    let left = rect.left;

    // Check if dropdown would overflow bottom
    if (top + dropdownHeight > window.innerHeight - padding) {
      top = rect.top - dropdownHeight - padding;
    }

    // Check if dropdown would overflow right
    if (left + dropdownWidth > window.innerWidth - padding) {
      left = window.innerWidth - dropdownWidth - padding;
    }

    // Check if dropdown would overflow left
    if (left < padding) {
      left = padding;
    }

    setDropdownPosition({ top, left });
  }, []);

  // Update position on open and scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();

    const handleScrollOrResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updateDropdownPosition]);

  // Click outside detection
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (dropdownRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;

      setIsOpen(false);
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleColorClick = (color: string) => {
    if (showOpacity && currentOpacity !== 100 && currentOpacity !== 0) {
      onChange(combineColorOpacity(color, currentOpacity));
    } else {
      onChange(color);
    }
    if (mode === 'popover') {
      setIsOpen(false);
    }
  };

  const handleOpacityChange = (opacity: number) => {
    onChange(combineColorOpacity(currentHex, opacity));
  };

  const handleTransparentClick = () => {
    onChange('transparent');
    if (mode === 'popover') {
      setIsOpen(false);
    }
  };

  const applyCustomColor = (hex: string) => {
    setHexDraft(hex.toUpperCase());
    if (showOpacity && currentOpacity !== 100 && currentOpacity !== 0) {
      onChange(combineColorOpacity(hex, currentOpacity));
    } else {
      onChange(hex);
    }
  };

  const handleCustomClick = () => {
    setHexDraft(value === 'transparent' ? '' : currentHex.toUpperCase());
    setShowCustomInput((prev) => !prev);
  };

  const commitHexDraft = () => {
    const match = hexDraft.trim().match(/^#?([0-9a-fA-F]{6})$/);
    if (match) {
      applyCustomColor(`#${match[1].toUpperCase()}`);
    }
  };

  // Render the color grid content
  const renderColorGrid = () => (
    <div className="p-3">
      {/* Color Grid - 6 columns x 4 rows */}
      <div className="grid grid-cols-6 gap-2 mb-3">
        {COLOR_PALETTE.map((color) => {
          const isSelected = currentHex.toLowerCase() === color.hex.toLowerCase() && value !== 'transparent';
          const isLight = color.category === 'neutral' && (color.hex === '#FFFFFF' || color.hex === '#E5E7EB' || color.hex === '#D1D5DB');

          return (
            <button
              key={color.id}
              type="button"
              onClick={() => handleColorClick(color.hex)}
              className={cn(
                'w-10 h-10 rounded-xl transition-all duration-150',
                'hover:scale-110 hover:shadow-md',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                isSelected && 'ring-2 ring-primary-500 ring-offset-2',
                isLight && !isSelected && 'border border-gray-200'
              )}
              style={{ backgroundColor: color.hex }}
              title={color.name}
              aria-label={`Select ${color.name}`}
              aria-pressed={isSelected}
            >
              {isSelected && (
                <Check
                  className={cn(
                    'w-5 h-5 mx-auto',
                    color.category === 'deep' || color.hex === '#000000' || color.hex === '#4B5563'
                      ? 'text-white'
                      : 'text-gray-800'
                  )}
                  strokeWidth={2.5}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Opacity Slider */}
      {showOpacity && value !== 'transparent' && (
        <div className="mb-3 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Opacity</span>
            <span className="text-sm text-gray-500 font-mono">{currentOpacity}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={currentOpacity}
            onChange={(e) => handleOpacityChange(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
          />
        </div>
      )}

      {/* Bottom Actions */}
      <div className="flex gap-2">
        {/* Theme Button */}
        {showThemeOption && (
          <button
            type="button"
            onClick={() => {
              onChange('theme');
              if (mode === 'popover') {
                setIsOpen(false);
              }
            }}
            className={cn(
              'flex-1 h-10 rounded-xl border-2 transition-all duration-150',
              'flex items-center justify-center gap-2',
              'text-sm font-medium',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              value === 'theme'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
            )}
          >
            <div
              className="w-4 h-4 rounded border border-gray-300"
              style={{ backgroundColor: themeResolvedColor || '#FFFFFF' }}
            />
            {value === 'theme' && <Check className="w-4 h-4" />}
            <span>Theme</span>
          </button>
        )}

        {/* Transparent Button */}
        {showTransparent && (
          <button
            type="button"
            onClick={handleTransparentClick}
            className={cn(
              'flex-1 h-10 rounded-xl border-2 transition-all duration-150',
              'flex items-center justify-center gap-2',
              'text-sm font-medium',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              value === 'transparent'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
            )}
            style={{
              backgroundImage: value !== 'transparent'
                ? 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)'
                : 'none',
              backgroundSize: '8px 8px',
              backgroundPosition: '0 0, 4px 4px'
            }}
          >
            {value === 'transparent' && <Check className="w-4 h-4" />}
            <span>None</span>
          </button>
        )}

        {/* Custom Color Button */}
        <button
          type="button"
          onClick={handleCustomClick}
          aria-expanded={showCustomInput}
          className={cn(
            'flex-1 h-10 rounded-xl border-2 border-gray-200 bg-white',
            'flex items-center justify-center gap-2',
            'text-sm font-medium text-gray-600',
            'hover:border-gray-300 hover:bg-gray-50',
            'transition-all duration-150',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            (showCustomInput || (isCustomColor(value) && value !== 'transparent')) &&
              'border-primary-500 bg-primary-50 text-primary-700'
          )}
        >
          <Palette className="w-4 h-4" />
          <span>Custom</span>
        </button>
      </div>

      {/* Custom color input row */}
      {showCustomInput && (
        <div className="mt-3 flex items-center gap-2">
          <input
            type="color"
            value={value === 'transparent' ? '#ffffff' : currentHex}
            onChange={(e) => applyCustomColor(e.target.value)}
            className="h-10 w-12 cursor-pointer rounded-lg border border-gray-200 bg-white p-1"
            aria-label={t('accessibility.customColorPicker')}
          />
          <input
            type="text"
            value={hexDraft}
            onChange={(e) => setHexDraft(e.target.value)}
            onBlur={commitHexDraft}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitHexDraft();
              }
            }}
            placeholder="#3B82F6"
            spellCheck={false}
            aria-label={t('accessibility.customColorPicker')}
            className="h-10 min-w-0 flex-1 rounded-xl border-2 border-gray-200 px-3 font-mono text-sm text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
          />
        </div>
      )}
    </div>
  );

  // Trigger button size variants
  const triggerSizes = {
    sm: { button: 'p-1 rounded', swatch: 'w-5 h-5 rounded', text: 'text-xs' },
    md: { button: 'p-1.5 rounded-md', swatch: 'w-6 h-6 rounded-md', text: 'text-sm' },
    lg: { button: 'p-2 rounded-lg', swatch: 'w-8 h-8 rounded-lg', text: 'text-sm' },
  };
  const triggerSize = triggerSizes[size];

  // Popover mode with portal
  if (mode === 'popover') {
    return (
      <div className={cn('relative', className)}>
        {/* Trigger Button */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => {
            setShowCustomInput(false);
            setIsOpen(!isOpen);
          }}
          className={cn(
            'flex items-center gap-1.5',
            triggerSize.button,
            'border border-gray-200 bg-white',
            'hover:border-gray-300 transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1'
          )}
          title={`Color: ${getColorName(value)}`}
        >
          {label && <span className={cn('text-gray-600', triggerSize.text)}>{label}</span>}
          <div
            className={cn(
              triggerSize.swatch,
              'border border-gray-200',
              value === 'transparent' && 'border-dashed',
              value === 'theme' && 'border-dotted border-2'
            )}
            style={{
              backgroundColor: value === 'transparent'
                ? '#ffffff'
                : value === 'theme'
                  ? (themeResolvedColor || '#FFFFFF')
                  : value,
              backgroundImage: value === 'transparent'
                ? 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)'
                : 'none',
              backgroundSize: '4px 4px',
              backgroundPosition: '0 0, 2px 2px'
            }}
          />
        </button>

        {/* Dropdown Portal */}
        {isOpen && dropdownPosition && createPortal(
          <div
            ref={dropdownRef}
            data-color-picker-portal="true"
            className="fixed z-[10001] bg-white rounded-2xl shadow-xl border border-gray-200"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: 280,
            }}
          >
            {renderColorGrid()}
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Inline mode
  return (
    <div className={cn('bg-white rounded-2xl border border-gray-200', className)}>
      {renderColorGrid()}
    </div>
  );
};

export default ColorPicker;
